import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { axiosInstance } from "../../api/axios-client";
import {
  useGetLatestTerm,
  useAcceptTerm,
  getDownloadTermByIdUrl,
  getGetTermAcceptanceByUserQueryKey,
} from "../../api/bpm/endpoints";
import type { TermsOfUseDto } from "../../api/bpm/model";
import { env } from "../../utils/env";
import { isSebraeMG } from "../../utils/envUtils";
import { isNotFoundError } from "../../utils/termsAccess";

export const Route = createFileRoute("/__auth/terms")({
  component: TermsComponent,
  staticData: {
    title: "terms.termsOfUse",
  },
});

function TermsComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isSebraeMG()) {
      navigate({ to: "/" });
    }
  }, [navigate]);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const {
    data: latestTermResponse,
    isLoading: latestTermLoading,
    error: latestTermError,
  } = useGetLatestTerm();

  useEffect(() => {
    if (isNotFoundError(latestTermError)) {
      navigate({ to: "/" });
    }
  }, [latestTermError, navigate]);

  const latestTerm = latestTermResponse as unknown as TermsOfUseDto | undefined;
  const termId = latestTerm?.id;

  const [termUrl, setTermUrl] = useState<string | undefined>(undefined);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if (!termId) return;

    let objectUrl: string | undefined;
    setPdfLoading(true);

    const bpmUrl = env.VITE_APP_SMARTBPM_URL || "";
    let bpmPrefix = "/api/smartbpm";
    try {
      if (bpmUrl && bpmUrl.startsWith("http")) {
        const urlObj = new URL(bpmUrl);
        const pathname = urlObj.pathname.replace(/\/$/, "");
        if (pathname && pathname !== "/") {
          bpmPrefix = `${pathname}/smartbpm`;
        }
      }
    } catch {
      // Mantém o prefixo padrão quando a URL configurada é inválida.
    }

    axiosInstance
      .get(`${bpmPrefix}${getDownloadTermByIdUrl(termId)}`, {
        responseType: "blob",
      })
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(blob);
        setTermUrl(objectUrl);
      })
      .catch(() => {
        setTermUrl(undefined);
      })
      .finally(() => {
        setPdfLoading(false);
      });

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [termId]);

  const acceptTermMutation = useAcceptTerm();

  const isLoading = latestTermLoading || pdfLoading;
  const hasError = latestTermError;

  const handleAcceptTerm = () => {
    if (!termId) return;

    const navigateHome = () => {
      queryClient.invalidateQueries({
        queryKey: getGetTermAcceptanceByUserQueryKey(termId),
      });
      setSnackbar({
        open: true,
        message: t("terms.termAccepted"),
        severity: "success",
      });
      setTimeout(() => {
        navigate({ to: "/" });
      }, 1500);
    };

    acceptTermMutation.mutate(
      { id: termId },
      {
        onSuccess: () => {
          navigateHome();
        },
        onError: (error: any) => {
          const status = error?.response?.status || error?.status;
          if (status === 400) {
            navigateHome();
            return;
          }
          setSnackbar({
            open: true,
            message: t("terms.errorLoadingTerm"),
            severity: "error",
          });
        },
      },
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {t("terms.termsOfUse")}
        </Typography>
      </Paper>

      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography color="text.secondary">
              {t("terms.loadingTerm")}
            </Typography>
          </Box>
        ) : hasError ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography color="error">{t("terms.errorLoadingTerm")}</Typography>
          </Box>
        ) : (
          <iframe
            src={termUrl}
            title={latestTerm?.name || t("terms.termsOfUse")}
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        )}
      </Box>

      <Paper
        elevation={0}
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "flex-end",
          flexShrink: 0,
        }}
      >
        <Button
          id="terms-accept-btn"
          onClick={handleAcceptTerm}
          variant="contained"
          disabled={acceptTermMutation.isPending || !termId}
          startIcon={
            acceptTermMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
        >
          {t("terms.acceptTerms")}
        </Button>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
